import { useState, useEffect } from "react";
import "./css/adminaccept.css";
import AdminStoreDetail from "./modal/AdminStoreDetail";
import { getReadyStores, searchAccept } from "../../utils/adminStoreService";
import { downloadPendingStoreExcel } from "../../utils/adminExcelService";
import Pagination from "../common/Pagination";
import { formatDateTime24 } from "../../utils/commonService";

const AdminAccept = () => {
  const [selectedStore, setSelectedStore] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);
  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;

  // 검색 여부 플래그
  const [isSearchMode, setIsSearchMode] = useState(false);

  // 오늘 날짜 구하기
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // 필터 관리
  const [filters, setFilters] = useState({
    bizType: "",
    startDate: "",
    endDate: getTodayDate(),
    searchType: "all",
    searchKeyword: "",
  });

  // 승인 대기 가맹점 목록 조회
  const loadStores = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getReadyStores();
      if (result.success) {
        setStores(result.data?.content || []);
        setCurrentPage(result.data.number);
        setTotalPages(result.data.totalPages);
        setTotalElements(result.data.totalElements);
        setIsSearchMode(false);
      } else {
        setError(result.error);
        setStores([]);
      }
    } catch (err) {
      setError("가맹점 목록을 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 목록 조회
  useEffect(() => {
    loadStores();
  }, []);

  const handleDetailClick = (store) => {
    setSelectedStore(store);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedStore(null);
  };

  // 승인/거절 후 목록 새로고침
  const handleRefresh = () => {
    loadStores();
  };

  // 검색 내부 함수
  const handleSearchInternal = async (page = 0) => {
    // 검색 파라미터 매칭
    const searchParams = {
      bizType: filters.bizType,
      startDate: filters.startDate,
      endDate: filters.endDate,
      searchTerm: filters.searchKeyword,
      searchType: filters.searchType,
    };

    setLoading(true);
    setError(null);

    try {
      const result = await searchAccept(searchParams, page, pageSize);
      if (result.success) {
        setStores(result.data.content);
        setCurrentPage(result.data.number);
        setTotalPages(result.data.totalPages);
        setTotalElements(result.data.totalElements);
        setIsSearchMode(true);
      } else {
        setError(result.error);
        alert(result.error);
      }
    } catch (err) {
      setError(err.message);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    isSearchMode ? handleSearchInternal(page) : loadStores(page);
  };

  // 검색 버튼 클릭
  const handleSearch = () => {
    // 페이지를 0으로 다시 초기화
    setCurrentPage(0);
    // 검색 기능 구현 함수
    handleSearchInternal(0);
  };

  const handleReset = () => {
    setFilters({
      bizType: "",
      startDate: "",
      endDate: getTodayDate(),
      searchType: "all",
      searchKeyword: "",
    });
    setCurrentPage(0);
    loadStores();
  };

  // 셀러 등록 가맹점 목록 엑셀 다운로드
  const handleExcelDownload = async () => {
    setExcelLoading(true);
    const result = await downloadPendingStoreExcel();
    setExcelLoading(false);
    if (!result.success) alert(result.error);
  };

  return (
    <div className="adminaccept-container">
      <div className="adminaccept-wrap">
        <div className="adminaccept-filter-wrap">
          <div className="adminaccept-filter-row">
            <div className="adminaccept-filter-group">
              <label htmlFor="accept-period">조회기간</label>
              <div className="adminaccept-date-group">
                <input
                  type="date"
                  id="adminaccept-start-date"
                  className="adminaccept-date-input"
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters({ ...filters, startDate: e.target.value })
                  }
                />
                <span className="adminaccept-date-separator">~</span>
                <input
                  type="date"
                  id="adminaccept-end-date"
                  className="adminaccept-date-input"
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters({ ...filters, endDate: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          <div className="adminaccept-filter-row adminaccept-search-row">
            <div className="adminaccept-search-group">
              <select
                name="type"
                id="type"
                className="adminaccept-select-type"
                value={filters.searchType}
                onChange={(e) =>
                  setFilters({ ...filters, searchType: e.target.value })
                }
              >
                <option value="all">전체</option>
                <option value="bizNm">상호명</option>
                <option value="bizNo">사업자번호</option>
                <option value="ownerNm">대표자명</option>
              </select>
              <input
                type="text"
                className="adminaccept-input"
                placeholder="검색어를 입력하세요"
                value={filters.searchKeyword}
                onChange={(e) =>
                  setFilters({ ...filters, searchKeyword: e.target.value })
                }
              />
              <button className="adminaccept-search-btn" onClick={handleSearch}>
                검색
              </button>
              <button className="adminaccept-reset-btn" onClick={handleReset}>
                초기화
              </button>
            </div>
          </div>
        </div>

        <div className="adminaccept-table-wrap">
          <div className="adminaccept-table-header">
            <div className="adminaccept-table-info">
              총 <span className="adminaccept-count">{totalElements}</span>건
            </div>
            <button
              className="adminaccept-excel-btn"
              onClick={handleExcelDownload}
              disabled={excelLoading}
            >
              {excelLoading ? "다운로드 중..." : "엑셀 다운로드"}
            </button>
          </div>

          {loading && <div className="adminaccept-loading">로딩 중...</div>}

          {error && <div className="adminaccept-error">{error}</div>}

          <table className="adminaccept-table">
            <thead>
              <tr>
                <th>상호명</th>
                <th>업종/업태</th>
                <th>사업자번호</th>
                <th>대표자명</th>
                <th>연락처</th>
                <th>등록일</th>
                <th>상세</th>
              </tr>
            </thead>
            <tbody>
              {!loading && !error && stores.length === 0 && (
                <tr>
                  <td colSpan="8" className="adminaccept-empty">
                    승인 대기 중인 가맹점이 없습니다.
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                stores.map((store, index) => (
                  <tr key={store.storeCode || index}>
                    <td>{store.storeNm || "-"}</td>
                    <td>{store.bizType || "-"}</td>
                    <td>{store.bizNo || "-"}</td>
                    <td>{store.ownerNm || "-"}</td>
                    <td>{store.ownerTelNo || "-"}</td>
                    <td>
                      {store.requestedDateTime
                        ? formatDateTime24(store.requestedDateTime)
                        : "-"}
                    </td>
                    <td>
                      <button
                        className="adminaccept-btn adminaccept-btn-detail"
                        onClick={() => handleDetailClick(store)}
                      >
                        보기
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      {showModal && selectedStore && (
        <AdminStoreDetail
          store={selectedStore}
          onClose={handleCloseModal}
          onRefresh={handleRefresh}
        />
      )}
    </div>
  );
};

export default AdminAccept;
